import { useCallback } from 'react';
import useNotification from '@/hooks/useNotification';
import { NotificationSeverity } from '@/components/notifications/Notification';
import { getErrorMessage } from '@/utils/errorUtils';
import { useCancelRouteMutation } from '../services';
import { getScopeErrorMessage } from '../utils/getScopeErrorMessage';

export interface UseCancelRouteParams {
  routeId: string;
  logisticOperatorId?: string | null;
  onSuccess?: () => void;
}

export function useCancelRoute({
  routeId,
  logisticOperatorId,
  onSuccess,
}: UseCancelRouteParams) {
  const { showNotification } = useNotification();
  const [cancelRouteMutation, { isLoading: isCancelling }] =
    useCancelRouteMutation();

  const cancelRoute = useCallback(async (): Promise<boolean> => {
    const operatorId = logisticOperatorId?.trim() ?? '';
    if (!operatorId) {
      showNotification(
        'El operador logístico es requerido para cancelar la ruta.',
        NotificationSeverity.error
      );
      return false;
    }

    try {
      const response = await cancelRouteMutation({
        routeId,
        logisticOperatorId: operatorId,
      }).unwrap();

      // La ruta se cancela en el acto, pero los pedidos se liberan por la cola
      // de Hermes. Si el batch no se encoló, decirlo: el pedido sigue tomado.
      const batchResult = response?.batchResult;
      if (batchResult && batchResult.success === false) {
        const failedNumbers = (batchResult.failed ?? [])
          .map((item) => item.shippingNumber)
          .join(', ');
        showNotification(
          `Ruta cancelada, pero no se pudo liberar ${
            failedNumbers ? `los pedidos ${failedNumbers}` : 'los pedidos'
          }. Siguen en su estado actual: avisá a TI.`,
          NotificationSeverity.warning
        );
      } else {
        showNotification(
          'Ruta cancelada. Los pedidos vuelven a Pendiente en unos segundos.',
          NotificationSeverity.success
        );
      }
      onSuccess?.();
      return true;
    } catch (error: unknown) {
      showNotification(
        getScopeErrorMessage(error) ??
          getErrorMessage(error, 'Error al cancelar la ruta'),
        NotificationSeverity.error
      );
      return false;
    }
  }, [
    routeId,
    logisticOperatorId,
    cancelRouteMutation,
    showNotification,
    onSuccess,
  ]);

  return { cancelRoute, isCancelling };
}

export default useCancelRoute;
